import React, { useCallback, useState } from "react";
import classNames from "classnames";
import { isEqual } from "@common/helpers/comparison";
import { omitBy } from "@common/helpers/objects";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import {
    FiltersChangeFn,
    TransactionsFilters,
} from "../../transactions/filters/lib";
import { FinancialDocumentDetailsView } from "./usePathBasedFinancialDocumentDetailsView";
import { FinancialDocumentDetailsHeader } from "./FinancialDocumentDetailsHeader";
import { MatchingViewHeader } from "./MatchingView/MatchingViewHeader";
import styles from "./FinancialDocumentDetails.module.scss";
import { FinancialDocumentPreview } from "./FinancialDocumentPreview";
import { MatchingView } from "./MatchingView/MatchingView";
import { FinancialDocumentMatches } from "./FinancialDocumentMatches/FinancialDocumentMatches";
import { useFinancialDocumentDetailsContext } from "./financialDocumentDetails.context";

interface FinancialDocumentDetailsProps {
    financialDocument: FinancialDocumentExtended;
}

export const FinancialDocumentDetails: React.FC<
    FinancialDocumentDetailsProps
> = ({ financialDocument }) => {
    const { currentView, changeView } = useFinancialDocumentDetailsContext();

    const initialFilters = (() => {
        if (financialDocument.amount == null) {
            return {};
        }
        const absAmount = Math.abs(financialDocument.amount);
        return { minAmount: absAmount, maxAmount: absAmount };
    })();

    const [transactionFilters, setTransactionFilters] =
        useState<TransactionsFilters>(initialFilters);

    const handleFiltersChange: FiltersChangeFn = useCallback(
        (value: Partial<TransactionsFilters>) => {
            const newFilters = omitBy(
                { ...transactionFilters, ...value },
                (v) => v === null || v === undefined,
            );

            if (!isEqual(newFilters, transactionFilters)) {
                setTransactionFilters(newFilters);
            }
        },
        [transactionFilters],
    );

    let content = null;
    let header = null;

    if (currentView === FinancialDocumentDetailsView.DEFAULT) {
        header = (
            <FinancialDocumentDetailsHeader
                financialDocument={financialDocument}
            />
        );
        const hasLinkedItems =
            financialDocument.transactionMatches.length > 0 ||
            financialDocument.linkedJournalEntries.length > 0 ||
            financialDocument.linkedInvoices.length > 0 ||
            financialDocument.linkedBills.length > 0 ||
            financialDocument.counterparty != null;
        content = hasLinkedItems ? (
            <FinancialDocumentMatches financialDocument={financialDocument} />
        ) : null;
    } else if (currentView === FinancialDocumentDetailsView.MATCHING) {
        header = (
            <MatchingViewHeader
                onBack={() => changeView(FinancialDocumentDetailsView.DEFAULT)}
            />
        );
        content = (
            <MatchingView
                financialDocument={financialDocument}
                filters={transactionFilters}
                onFiltersChange={handleFiltersChange}
                clearFilters={() => setTransactionFilters({})}
            />
        );
    }

    return (
        <div
            className={classNames(styles.layout, {
                [styles.defaultView]:
                    currentView === FinancialDocumentDetailsView.DEFAULT,
                [styles.matchingView]:
                    currentView === FinancialDocumentDetailsView.MATCHING,
            })}
        >
            <div className={styles.previewArea}>
                <FinancialDocumentPreview
                    financialDocument={financialDocument}
                />
            </div>
            <div className={styles.headerArea}>{header}</div>
            <div className={styles.contentArea}>{content}</div>
        </div>
    );
};
